import { Text } from "react-native-paper";
import type { IconType } from "../../icon";
import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
import Box from "../Box";
import { StatTile, type StatTileCta } from "../StatTile";
import { TipCallout } from "../TipCallout";

export type OverviewStat = {
	id: string;
	icon: IconType;
	value: string;
	label: string;
	tooltip?: string;
	skillId?: string;
	cta?: StatTileCta;
	onPress?: () => void;
	isLoading?: boolean;
};

type ProgressOverviewProps = {
	title: string;
	subtitle?: React.ReactNode;
	stats: OverviewStat[];
	tip?: string;
	tipLoading?: boolean;
};

const TILE_GAP = 2;

const StatTileCell = ({ stat }: { stat: OverviewStat }) => {
	const theme = useAppTheme();

	return (
		<Box flex={1} minWidth={0} backgroundColor={theme.colors.surface}>
			<StatTile
				icon={stat.icon}
				label={stat.label}
				value={stat.value}
				tooltip={stat.tooltip}
				cta={stat.cta}
				onPress={stat.onPress}
				isLoading={stat.isLoading}
				outlined={false}
			/>
		</Box>
	);
};

export const ProgressOverview = ({
	title,
	subtitle,
	stats,
	tip,
	tipLoading,
}: ProgressOverviewProps) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();

	const maxColumns = isMobile ? 2 : 4;
	const rows = Math.ceil(stats.length / maxColumns);
	const columns = Math.max(1, Math.ceil(stats.length / rows));
	const statRows: OverviewStat[][] = [];
	for (let index = 0; index < stats.length; index += columns) {
		statRows.push(stats.slice(index, index + columns));
	}

	return (
		<Box gap={TILE_GAP} borderRadius={theme.roundness * 3} overflow="hidden">
			<Box
				backgroundColor={theme.colors.surface}
				alignItems="center"
				gap={12}
				paddingVertical={24}
				paddingHorizontal={16}
			>
				<Text variant="headlineMedium" style={{ textAlign: "center" }}>
					{title}
				</Text>
				{subtitle ? (
					<Text
						variant="bodyMedium"
						style={{
							textAlign: "center",
							color: theme.colors.onSurfaceVariant,
						}}
					>
						{subtitle}
					</Text>
				) : null}
			</Box>
			{statRows.map((row) => (
				<Box
					key={row.map((stat) => stat.id).join("-")}
					flexDirection="row"
					gap={TILE_GAP}
				>
					{row.map((stat) => (
						<StatTileCell key={stat.id} stat={stat} />
					))}
				</Box>
			))}
			{tip || tipLoading ? (
				<Box backgroundColor={theme.colors.surface} padding={16}>
					<TipCallout text={tip} isLoading={tipLoading} />
				</Box>
			) : null}
		</Box>
	);
};
